import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';
import { Input } from '../Input';
import { Modal } from '../Modal';
import { Select } from '../Select';
import { TextArea } from '../TextArea';
import { api } from '../../lib/api';
import { useT } from '../../i18n';
import type { TranslationKey } from '../../i18n';
import {
  CONFIDENCE_REQUIRED_STAGES,
  nextStage,
  nextStageFor,
  previousStageFor,
  type Confidence,
  type Inquiry,
  type InquiryAttachment,
  type InteractionMode,
  type ProductType,
  type Stage,
} from '../../types/api';
import styles from './TransitionMenu.module.css';

/** Transition types the UI can submit (subset of the server contract). */
export type TransitionAction =
  | 'ADVANCE'
  | 'DROP'
  | 'PAUSE'
  | 'RESUME'
  | 'WIN'
  | 'SET_CONFIDENCE'
  | 'ROLLBACK';

export interface TransitionInput {
  type: TransitionAction;
  confidence?: Confidence;
  reason?: string;
  /** ISO date-time — optional reminder date for PAUSE (issue #25). */
  remindAt?: string;
  /** Work mode — required on the PRIORITIZATION → REALIZATION advance. */
  interactionMode?: InteractionMode;
  /** ISO date-time — optional on-site audit date (only with AUDIT). */
  auditAt?: string;
  /** Concept description — required on the REALIZATION → INTEGRATOR_SELECTION
   *  advance unless a CONCEPT artifact is attached. */
  conceptSummary?: string;
  conceptApprovedByClient?: boolean;
  /** Product line — chosen on the VALIDATION → VERIFICATION advance. */
  productType?: ProductType;
  /** Explicit ADVANCE target — only the educational «Нет, перейти к ТКП» skip
   *  (VERIFICATION → TKP) is legal. */
  toStage?: Stage;
}

export interface TransitionMenuProps {
  inquiry: Inquiry;
  /** Manufacturer sees the full action set; integrators only
   *  PAUSE / RESUME / SET_CONFIDENCE (matches server RBAC). */
  isManufacturer: boolean;
  /** Disable everything while a transition request is in flight. */
  pending?: boolean;
  /** Must resolve on success and reject (ideally ApiError) on failure —
   *  the message is surfaced inside the open modal. */
  onAction: (input: TransitionInput) => Promise<void>;
  /** Customer feedback — the confidence control lives next to the
   *  confidence badge in the drawer meta row; these props let that
   *  control open this menu's SET_CONFIDENCE modal (single dialog,
   *  unchanged logic). */
  confidenceModalOpen?: boolean;
  onConfidenceModalClose?: () => void;
}

const STAGE_LABEL_KEY: Record<Stage, TranslationKey> = {
  VALIDATION: 'stage.validation',
  VERIFICATION: 'stage.verification',
  PRIORITIZATION: 'stage.prioritization',
  REALIZATION: 'stage.realization',
  INTEGRATOR_SELECTION: 'stage.integratorSelection',
  TKP: 'stage.tkp',
  CONTRACT: 'stage.contract',
};

const CONFIDENCE_OPTIONS: { value: Confidence; labelKey: TranslationKey }[] = [
  { value: 'K90', labelKey: 'confidence.k90' },
  { value: 'K50', labelKey: 'confidence.k50' },
  { value: 'K10', labelKey: 'confidence.k10' },
];

const INTERACTION_MODE_OPTIONS: { value: InteractionMode; labelKey: TranslationKey }[] = [
  { value: 'AUDIT', labelKey: 'transitions.modeAudit' },
  { value: 'REMOTE_TZ', labelKey: 'transitions.modeRemoteTz' },
];

const PRODUCT_TYPE_OPTIONS: { value: ProductType; labelKey: TranslationKey }[] = [
  { value: 'INTEGRATION_PROJECT', labelKey: 'transitions.productTypeIntegration' },
  { value: 'EDUCATIONAL_ROBOT', labelKey: 'transitions.productTypeEducational' },
];

/**
 * Deal action bar for the Board: Дальше / Пауза / Возобновить / Отказ /
 * Выиграно / Задать уверенность. Actions that need a reason and/or a
 * confidence rating open a small Modal; mirrors the server validation
 * (422 reason/confidence required) client-side and also shows the
 * server message when the API rejects.
 */
export function TransitionMenu({ inquiry, isManufacturer, pending, onAction, confidenceModalOpen, onConfidenceModalClose }: TransitionMenuProps) {
  const { t } = useT();
  const [modalFor, setModalFor] = useState<TransitionAction | null>(null);
  const [reason, setReason] = useState('');
  const [remindAt, setRemindAt] = useState('');
  const [confidence, setConfidence] = useState<Confidence | ''>('');
  const [interactionMode, setInteractionMode] = useState<InteractionMode | ''>('');
  const [auditAt, setAuditAt] = useState('');
  const [conceptSummary, setConceptSummary] = useState('');
  const [conceptApproved, setConceptApproved] = useState(false);
  const [productType, setProductType] = useState<ProductType | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [emailBody, setEmailBody] = useState('');
  const [copied, setCopied] = useState(false);
  const emailDirty = useRef(false);

  // External entry point (confidence badge in the drawer meta row)
  useEffect(() => {
    if (confidenceModalOpen) openModal('SET_CONFIDENCE');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confidenceModalOpen]);

  // Terminal deals (WON/LOST) accept no further transitions — the single
  // exception is «Откатить на шаг назад» (issue #65), which revives the deal
  // into the funnel on its last active stage. Integrators never get it (403).
  const terminal = inquiry.outcome !== 'NONE';
  if (terminal && !isManufacturer) return null;

  const next = nextStageFor(inquiry.stage, inquiry.productType);
  const lateStage = CONFIDENCE_REQUIRED_STAGES.includes(inquiry.stage);
  /** Issue #65 — rollback target: the previous stage along the deal's route,
   *  or (for a terminal deal) the last active stage it is still parked on. */
  const rollbackTarget: Stage | null = terminal
    ? inquiry.stage
    : previousStageFor(inquiry.stage, inquiry.productType);
  const canRollback = isManufacturer && rollbackTarget !== null;

  /** ADVANCE is blocked until the deal has a responsible manager and a client
   *  (mirrors the server 422s in planTransition). Null when advanceable. */
  const advanceBlockReason = next
    ? !inquiry.assignedManagerId
      ? t('transitions.advanceBlockedNoManager')
      : !inquiry.clientId
        ? t('transitions.advanceBlockedNoClient')
        : null
    : null;

  /** Does this action need the input modal (reason and/or confidence)? */
  const needsConfidence = (action: TransitionAction): boolean => {
    if (action === 'SET_CONFIDENCE') return true;
    if (action === 'ADVANCE') {
      const target = nextStageFor(inquiry.stage, inquiry.productType);
      return !!target && CONFIDENCE_REQUIRED_STAGES.includes(target) && !inquiry.confidence;
    }
    if (action === 'WIN') return !inquiry.confidence;
    return false;
  };

  const needsReason = (action: TransitionAction): 'required' | 'optional' | 'none' => {
    if (action === 'PAUSE' || action === 'DROP') return 'required';
    if (action === 'SET_CONFIDENCE') {
      // server: required on PRIORITIZATION+ unless a reason already exists
      return lateStage && !inquiry.confidenceReason ? 'required' : 'optional';
    }
    if (action === 'WIN') return 'optional';
    return 'none';
  };

  /** ADVANCE «Опция возможностей» → «Концепт проекта» requires picking the
   *  work mode (on-site audit / remote TOR proposal) — server-enforced 422.
   *  The choice lives inside this modal, so ADVANCE always opens it here. */
  const needsInteractionMode = (action: TransitionAction): boolean =>
    action === 'ADVANCE' && inquiry.stage === 'PRIORITIZATION';

  /** ADVANCE «Концепт проекта» → «Выбор интегратора» requires recording what
   *  was proposed to the client — text, or an uploaded CONCEPT artifact. */
  const needsConcept = (action: TransitionAction): boolean =>
    action === 'ADVANCE' && inquiry.stage === 'REALIZATION';

  /** First advance (VALIDATION → VERIFICATION) — pick the product line once. */
  const needsProductType = (action: TransitionAction): boolean =>
    action === 'ADVANCE' && inquiry.stage === 'VALIDATION' && !inquiry.productType;

  /** «Учебный робот» fork after Верификация: concept first or straight to TKP. */
  const needsConceptFork = (action: TransitionAction): boolean =>
    action === 'ADVANCE' &&
    inquiry.stage === 'VERIFICATION' &&
    inquiry.productType === 'EDUCATIONAL_ROBOT';

  // CONCEPT artifacts substitute for the text — check only when relevant.
  const attachmentsQ = useQuery<InquiryAttachment[]>({
    queryKey: ['inquiries', inquiry.id, 'attachments'],
    queryFn: () => api.get<InquiryAttachment[]>(`/inquiries/${inquiry.id}/attachments`),
    enabled: inquiry.stage === 'REALIZATION',
  });
  const hasConceptArtifact = (attachmentsQ.data ?? []).some((a) => a.kind === 'CONCEPT');
  const hasStoredConcept = Boolean(inquiry.conceptSummary?.trim()) || hasConceptArtifact;

  const openModal = (action: TransitionAction) => {
    setModalFor(action);
    setReason('');
    setRemindAt('');
    setConfidence(inquiry.confidence ?? '');
    setInteractionMode('');
    setAuditAt('');
    setConceptSummary(inquiry.conceptSummary ?? '');
    setConceptApproved(inquiry.conceptApprovedByClient ?? false);
    setProductType('');
    setError(null);
    emailDirty.current = false;
    setCopied(false);
    setEmailBody(hasEmailTemplate(action) ? buildEmailBody(action, '') : '');
  };

  /** PAUSE / DROP also show a template letter the manager sends manually. */
  const hasEmailTemplate = (action: TransitionAction | null): action is 'PAUSE' | 'DROP' =>
    action === 'PAUSE' || action === 'DROP';

  const buildEmailBody = (action: 'PAUSE' | 'DROP', reasonText: string): string =>
    t(action === 'PAUSE' ? 'emailTemplates.pause' : 'emailTemplates.drop', {
      summary: inquiry.summary,
      reason: reasonText.trim() || '—',
    });

  const recipient = inquiry.sourceEmail || inquiry.client?.contactEmail || null;

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(emailBody);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable (e.g. non-secure context) — nothing to surface
    }
  };

  const handleReasonChange = (value: string) => {
    setReason(value);
    if (!emailDirty.current && hasEmailTemplate(modalFor)) {
      setEmailBody(buildEmailBody(modalFor, value));
    }
  };

  const closeModal = () => {
    if (submitting) return;
    setModalFor(null);
    setError(null);
    onConfidenceModalClose?.();
  };

  const run = async (input: TransitionInput) => {
    setSubmitting(true);
    setError(null);
    try {
      await onAction(input);
      setModalFor(null);
      onConfidenceModalClose?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('inquiries.toastTransitionFail'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClick = (action: TransitionAction) => {
    const withModal =
      action === 'ROLLBACK' ||
      needsConfidence(action) ||
      needsReason(action) !== 'none' ||
      needsInteractionMode(action) ||
      needsConcept(action) ||
      needsProductType(action) ||
      needsConceptFork(action);
    if (withModal) {
      openModal(action);
    } else {
      void run({ type: action });
    }
  };

  const submitModal = (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalFor || submitting) return;
    const reasonNeed = needsReason(modalFor);
    const trimmed = reason.trim();
    if (reasonNeed === 'required' && !trimmed) {
      setError(t('transitions.reasonRequired'));
      return;
    }
    if (needsConfidence(modalFor) && !confidence) {
      setError(
        modalFor === 'ADVANCE' && next
          ? t('transitions.confidenceRequiredAdvance', { stage: t(STAGE_LABEL_KEY[next]) })
          : t('transitions.reasonRequired'),
      );
      return;
    }
    if (needsInteractionMode(modalFor) && !interactionMode) {
      setError(t('transitions.interactionModeRequired'));
      return;
    }
    if (needsProductType(modalFor) && !productType) {
      setError(t('transitions.productTypeRequired'));
      return;
    }
    if (needsConcept(modalFor) && !hasStoredConcept && !conceptSummary.trim()) {
      setError(t('transitions.conceptRequired'));
      return;
    }
    void run({
      type: modalFor,
      // Only send the rating when the modal actually asked for it — otherwise
      // a stale stored value trips the server's early-stage confidence guard.
      ...(needsConfidence(modalFor) && confidence ? { confidence } : {}),
      ...(trimmed ? { reason: trimmed } : {}),
      ...(modalFor === 'PAUSE' && remindAt
        ? { remindAt: new Date(`${remindAt}T00:00:00`).toISOString() }
        : {}),
      ...(modalFor === 'ADVANCE' && interactionMode
        ? {
            interactionMode,
            ...(interactionMode === 'AUDIT' && auditAt
              ? { auditAt: new Date(`${auditAt}T00:00:00`).toISOString() }
              : {}),
          }
        : {}),
      ...(modalFor === 'ADVANCE' && productType ? { productType } : {}),
      ...(modalFor === 'ADVANCE' && needsConcept(modalFor)
        ? {
            ...(conceptSummary.trim() ? { conceptSummary: conceptSummary.trim() } : {}),
            conceptApprovedByClient: conceptApproved,
          }
        : {}),
    });
  };

  /** Educational fork answers: «Да» = default route (REALIZATION),
   *  «Нет» = explicit skip VERIFICATION → TKP. */
  const submitConceptFork = (skipConcept: boolean) => {
    if (submitting) return;
    const target: Stage = skipConcept ? 'TKP' : 'REALIZATION';
    if (needsConfidence('ADVANCE') && !confidence) {
      setError(t('transitions.confidenceRequiredAdvance', { stage: t(STAGE_LABEL_KEY[target]) }));
      return;
    }
    void run({
      type: 'ADVANCE',
      ...(needsConfidence('ADVANCE') && confidence ? { confidence } : {}),
      ...(skipConcept ? { toStage: 'TKP' as Stage } : {}),
    });
  };

  const busy = pending || submitting;
  const stageName = (s: Stage) => t(STAGE_LABEL_KEY[s]);

  return (
    <div className={styles.menu}>
      <div className={styles.actions}>
        {!terminal && isManufacturer && next && (
          <Button
            size="sm"
            onClick={() => handleClick('ADVANCE')}
            disabled={busy || inquiry.paused || advanceBlockReason !== null}
            title={advanceBlockReason ?? t('transitions.nextStageHint', { stage: stageName(next) })}
          >
            {t('transitions.advanceForward')}
          </Button>
        )}
        {!terminal && isManufacturer && (inquiry.stage === 'REALIZATION' || inquiry.stage === 'TKP') && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleClick('WIN')}
            disabled={busy || inquiry.paused}
          >
            {t('transitions.win')}
          </Button>
        )}
        {!terminal &&
          (inquiry.paused ? (
            <Button size="sm" variant="secondary" onClick={() => handleClick('RESUME')} disabled={busy}>
              {t('transitions.resume')}
            </Button>
          ) : (
            <Button size="sm" variant="warning" onClick={() => handleClick('PAUSE')} disabled={busy}>
              {t('transitions.pause')}
            </Button>
          ))}
        {!terminal && isManufacturer && (
          <Button size="sm" variant="danger" onClick={() => handleClick('DROP')} disabled={busy}>
            {t('transitions.drop')}
          </Button>
        )}
        {/* Issue #65 — roll back one stage (or revive a terminal deal). */}
        {canRollback && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => handleClick('ROLLBACK')}
            disabled={busy || (!terminal && inquiry.paused)}
            title={
              rollbackTarget
                ? t('transitions.rollbackHint', { stage: stageName(rollbackTarget) })
                : undefined
            }
          >
            {t('transitions.rollback')}
          </Button>
        )}
      </div>

      <Modal
        open={modalFor !== null}
        onClose={closeModal}
        title={
          modalFor
            ? modalFor === 'ADVANCE' && needsConceptFork('ADVANCE')
              ? t('transitions.forkTitle')
              : modalTitle(modalFor)
            : ''
        }
        size={
          hasEmailTemplate(modalFor) ||
          (modalFor === 'ADVANCE' && needsConceptFork('ADVANCE'))
            ? 'md'
            : 'sm'
        }
        footer={
          modalFor === 'ADVANCE' && needsConceptFork('ADVANCE') ? (
            <>
              <Button variant="secondary" onClick={closeModal} disabled={submitting}>
                {t('common.cancel')}
              </Button>
              <Button variant="secondary" onClick={() => submitConceptFork(true)} loading={submitting}>
                {t('transitions.forkNoTkp')}
              </Button>
              <Button onClick={() => submitConceptFork(false)} loading={submitting}>
                {t('transitions.forkYesConcept')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={closeModal} disabled={submitting}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" form="transition-form" loading={submitting}>
                {modalFor === 'ROLLBACK' ? t('transitions.rollbackSubmit') : t('transitions.submit')}
              </Button>
            </>
          )
        }
      >
        <form id="transition-form" onSubmit={submitModal} className={styles.form} noValidate>
          {modalFor === 'ADVANCE' && needsConceptFork('ADVANCE') && (
            <p className={styles.forkQuestion}>{t('transitions.forkQuestion')}</p>
          )}
          {/* Issue #65 — plain confirmation: rolling back a stage (or reviving
              a terminal deal) is reversible and touches nothing but the stage. */}
          {modalFor === 'ROLLBACK' && rollbackTarget && (
            <p className={styles.forkQuestion}>
              {terminal
                ? t('transitions.rollbackConfirmTerminal', { stage: stageName(rollbackTarget) })
                : t('transitions.rollbackConfirm', { stage: stageName(rollbackTarget) })}
            </p>
          )}
          {modalFor && needsProductType(modalFor) && (
            <Select
              label={t('transitions.productTypeLabel')}
              value={productType}
              onChange={(e) => setProductType(e.target.value as ProductType | '')}
              required
              fullWidth
              placeholder={t('transitions.productTypePlaceholder')}
              options={PRODUCT_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          )}
          {modalFor && needsConfidence(modalFor) && (
            <Select
              label={t('transitions.chooseConfidence')}
              value={confidence}
              onChange={(e) => setConfidence(e.target.value as Confidence | '')}
              required
              fullWidth
              placeholder={t('transitions.confidencePlaceholder')}
              options={CONFIDENCE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            />
          )}
          {modalFor && needsInteractionMode(modalFor) && (
            <>
              <Select
                label={t('transitions.interactionModeLabel')}
                value={interactionMode}
                onChange={(e) => setInteractionMode(e.target.value as InteractionMode | '')}
                required
                fullWidth
                placeholder={t('transitions.interactionModePlaceholder')}
                options={INTERACTION_MODE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
              />
              {interactionMode === 'AUDIT' && (
                <Input
                  type="date"
                  label={t('transitions.auditDateLabel')}
                  value={auditAt}
                  onChange={(e) => setAuditAt(e.target.value)}
                  fullWidth
                />
              )}
            </>
          )}
          {modalFor && needsConcept(modalFor) && (
            <>
              <TextArea
                label={t('transitions.conceptLabel')}
                value={conceptSummary}
                onChange={(e) => setConceptSummary(e.target.value)}
                placeholder={t('transitions.conceptPlaceholder')}
                helperText={hasConceptArtifact ? t('transitions.conceptArtifactHint') : undefined}
                rows={4}
                required={!hasStoredConcept}
                fullWidth
              />
              <Checkbox
                label={t('transitions.conceptApprovedLabel')}
                checked={conceptApproved}
                onChange={(e) => setConceptApproved(e.target.checked)}
              />
            </>
          )}
          {modalFor && needsReason(modalFor) !== 'none' && (
            <TextArea
              label={
                needsReason(modalFor) === 'required'
                  ? t('transitions.reasonPrompt')
                  : t('transitions.reasonOptional')
              }
              value={reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              placeholder={t('transitions.reasonPlaceholder')}
              rows={3}
              required={needsReason(modalFor) === 'required'}
              fullWidth
            />
          )}
          {modalFor === 'PAUSE' && (
            <Input
              type="date"
              label={t('transitions.remindAtLabel')}
              helperText={t('transitions.remindAtHint')}
              value={remindAt}
              onChange={(e) => setRemindAt(e.target.value)}
              fullWidth
            />
          )}
          {hasEmailTemplate(modalFor) && (
            <div className={styles.emailBlock}>
              <div className={styles.recipient}>
                <span className={styles.recipientLabel}>{t('emailTemplates.to')}:</span>{' '}
                {recipient ? (
                  <span data-testid="email-recipient">{recipient}</span>
                ) : (
                  <span className={styles.noAddress} role="note">
                    {t('emailTemplates.noAddress')}
                  </span>
                )}
              </div>
              <TextArea
                label={t('emailTemplates.bodyLabel')}
                value={emailBody}
                onChange={(e) => {
                  emailDirty.current = true;
                  setEmailBody(e.target.value);
                }}
                rows={8}
                fullWidth
              />
              <div className={styles.emailFooter}>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void copyEmail()}
                  disabled={!emailBody}
                >
                  {copied ? t('emailTemplates.copied') : t('emailTemplates.copy')}
                </Button>
                <span className={styles.emailHelper}>{t('emailTemplates.helper')}</span>
              </div>
            </div>
          )}
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </form>
      </Modal>
    </div>
  );

  function modalTitle(action: TransitionAction): string {
    switch (action) {
      case 'ADVANCE':
        return next ? t('transitions.advanceTo', { stage: stageName(next) }) : t('transitions.advance');
      case 'WIN':
        return t('transitions.win');
      case 'PAUSE':
        return t('transitions.pause');
      case 'DROP':
        return t('transitions.drop');
      case 'SET_CONFIDENCE':
        return t('transitions.setConfidence');
      case 'ROLLBACK':
        return t('transitions.rollback');
      default:
        return t('transitions.advance');
    }
  }
}
